import React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../store";
import { fetchTruckHistories } from "../../../store/features/truckHistorySlice";
import { fetchAllTrucks, selectAllTrucks } from "../../../store/features/allTrucksSlice";
import AddTruckHistoryForm from "../AddTruckHistoryForm";
import TruckRowWithHistory from "./TruckRowWithHistory";
import type { Truck } from "../../../types/truck";
import type { City } from "../../../types/city";
import type { PaginationResponse, PaginationParam } from "../../../types/paginationResponse";
import DataTable, { type TableFilters } from "../../common/Table";
import { TruckStatus } from "../../../types/truck";
import { formatLicensePlateInput } from "../../../utils/formatLicensePlate";
import type { TranslationKeys } from "./utils";
import { TestId } from "../../../constants/testIds";
import { isUserAdmin } from "../../../types/user";
type SortByField = "type" | "cityId" | "status" | "timestamp" | "destinationCityId";

interface TruckTableFilters {
  licensePlate?: string;
  status?: string;
  cityId?: string;
  destinationCityId?: string;
  type?: string;
  sortBy?: SortByField;
  sortOrder?: "asc" | "desc";
}

interface TruckTableProps {
  trucks: Truck[];
  cities: City[];
  onSearch?: (filters: TruckTableFilters, pagination: PaginationParam) => Promise<void>;
  showSearch?: boolean;
  pagination?: PaginationResponse | null;
  onPaginationChange?: (pagination: PaginationParam) => void;
  isAdmin?: boolean;
  loading?: boolean;
  error?: string | null;
  showRowsPerPage?: boolean;
  hideTypeFilter?: boolean;
}

const TruckTable: React.FC<TruckTableProps> = ({
  trucks,
  cities,
  onSearch,
  showSearch = true,
  pagination,
  onPaginationChange,
  loading = false,
  error = null,
  showRowsPerPage = true,
  hideTypeFilter = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [addHistoryOpen, setAddHistoryOpen] = useState<string | null>(null);
  const [localTrucks, setLocalTrucks] = useState<Truck[]>(trucks);
  const { user } = useSelector((state: RootState) => state.user);
  const allTrucks = useSelector(selectAllTrucks);
  const isAdmin = isUserAdmin(user);

  // Update local trucks when props change
  React.useEffect(() => {
    setLocalTrucks(trucks);
  }, [trucks]);

  React.useEffect(() => {
    dispatch(fetchAllTrucks());
  }, [dispatch]);

  // Validate that onSearch is defined when showSearch is true
  if (showSearch && !onSearch) {
    throw new Error("onSearch prop is required when showSearch is true");
  }

  const getCityName = (cityId: string) => {
    const city = cities.find((c) => c._id === cityId);
    return city ? city.name : cityId;
  };

  const handleAddHistory = (truckId: string) => {
    setAddHistoryOpen(truckId);
  };

  const handleAddHistorySuccess = (truckId: string) => {
    // Only refresh the truck history
    dispatch(
      fetchTruckHistories({
        truckId,
        page: 1,
        pageSize: 10,
      }),
    );
  };

  const handleTruckUpdate = (updatedTruck: Truck) => {
    setLocalTrucks((prevTrucks) =>
      prevTrucks.map((truck) => (truck._id === updatedTruck._id ? updatedTruck : truck)),
    );
  };

  const handleTableSearch = async (tableFilters: TableFilters, pagination: PaginationParam) => {
    if (!onSearch) return;

    // Convert TableFilters to SearchFilters
    const filters: TruckTableFilters = {
      licensePlate: tableFilters.licensePlate as string,
      status: tableFilters.status as string,
      cityId: tableFilters.cityId as string,
      destinationCityId: tableFilters.destinationCityId as string,
      type: tableFilters.type as string,
      sortBy: tableFilters.sortBy as SortByField,
      sortOrder: tableFilters.sortOrder as "asc" | "desc",
    };

    await onSearch(filters, pagination);
  };

  return (
    <>
      <DataTable
        data={localTrucks}
        columns={[
          {
            id: "expander",
            label: "",
            sortable: false,
          },
          {
            id: "licensePlate",
            label: t("License Plate"),
            sortable: true,
          },
          {
            id: "status",
            label: t("Status"),
            sortable: true,
          },
          {
            id: "cityId",
            label: t("Location"),
            sortable: true,
          },
          {
            id: "destination",
            label: t("Destination"),
            sortable: true,
          },
          {
            id: "lastUpdate",
            label: t("Last Update"),
            sortable: true,
          },
          {
            id: "source",
            label: t("Source"),
            sortable: false,
          },
          ...(isAdmin
            ? [
                {
                  id: "actions",
                  label: "",
                  sortable: false,
                },
              ]
            : []),
        ]}
        onSearch={handleTableSearch}
        showSearch={showSearch}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        renderRow={(truck, { isExpanded, onToggle }) => (
          <TruckRowWithHistory
            key={truck._id}
            truck={truck}
            isExpanded={isExpanded}
            onToggleRow={onToggle}
            onAddHistory={handleAddHistory}
            getCityName={getCityName}
            onTruckUpdate={handleTruckUpdate}
            cities={cities}
          />
        )}
        searchFilters={[
          {
            id: "licensePlate",
            label: t("License Plate"),
            type: "text" as const,
            options: allTrucks.map((truck) => ({
              value: truck.licensePlate,
              label: formatLicensePlateInput(truck.licensePlate),
            })),
            formatter: formatLicensePlateInput,
          },
          {
            id: "status",
            label: t("Status"),
            type: "select" as const,
            options: Object.values(TruckStatus).map((status) => ({
              value: status,
              label: t(status),
            })),
          },
          ...(hideTypeFilter
            ? []
            : [
                {
                  id: "type",
                  label: t("Type"),
                  type: "select" as const,
                  options: Array.from(new Set(allTrucks.map((truck) => truck.type))).map(
                    (type) => ({
                      value: type,
                      label: t(type as TranslationKeys),
                    }),
                  ),
                },
              ]),
          {
            id: "cityId",
            label: t("Location"),
            type: "select" as const,
            options: cities.map((city) => ({
              value: city._id,
              label: city.name,
            })),
          },
          {
            id: "destinationCityId",
            label: t("Destination"),
            type: "select" as const,
            options: cities.map((city) => ({
              value: city._id,
              label: city.name,
            })),
          },
        ]}
        loading={loading}
        showRowsPerPage={showRowsPerPage}
        testId={TestId.TRUCK.TABLE}
      />
      {addHistoryOpen && (
        <AddTruckHistoryForm
          open={true}
          onClose={() => setAddHistoryOpen(null)}
          truckId={addHistoryOpen}
          onSuccess={() => handleAddHistorySuccess(addHistoryOpen)}
          onTruckUpdate={handleTruckUpdate}
          trucks={localTrucks}
        />
      )}
    </>
  );
};

export default TruckTable;
